/**
 * Conversation actions.
 *
 * Two kinds live here. The plain creators at the top are what the command
 * router dispatches when something arrives over the channel. The thunks below
 * are what the UI calls — opening a conversation, sending a message — and
 * they only ever talk to the server. Neither reads the browser for a
 * transcript, because the browser does not hold one.
 */

import {
  TURN_STARTED,
  TURN_STATUS,
  TURN_THINKING,
  MESSAGE_DELTA,
  MESSAGE_COMPLETE,
  TURN_FAILED,
  CONFIRMATION_REQUIRED,
  LOAD_TRANSCRIPT,
  SET_ACTIVE_CONVERSATION,
  SET_CONVERSATIONS,
  CONVERSATION_UPSERTED,
  CONVERSATION_REMOVED,
  SET_CONVERSATIONS_LOADING,
  TURN_STOPPED,
  SET_TURN_FEEDBACK,
} from "./actionTypes";
import * as api from "../utils/conversationApi";

const LAST_CONVERSATION_KEY = "lou:last_conversation";

// ── From the wire ───────────────────────────────────────────────────────────

export const turnStarted = ({ turnId, userText, turnSeq }) => ({
  type: TURN_STARTED,
  payload: { turnId, userText, turnSeq },
});

export const turnStatus = ({ phase, node }) => ({
  type: TURN_STATUS,
  payload: { phase, node },
});

export const turnThinking = ({ turnId, text }) => ({
  type: TURN_THINKING,
  payload: { turnId, text },
});

export const messageDelta = ({ turnId, text }) => ({
  type: MESSAGE_DELTA,
  payload: { turnId, text },
});

export const messageComplete = ({ turnId, text, mode, traceId, suggestions }) => ({
  type: MESSAGE_COMPLETE,
  payload: { turnId, text, mode, traceId, suggestions },
});

export const turnFailed = ({ turnId, errorCode, message }) => ({
  type: TURN_FAILED,
  payload: { turnId, errorCode, message },
});

export const turnStopped = ({ turnId, text }) => ({
  type: TURN_STOPPED,
  payload: { turnId, text },
});

export const confirmationRequired = ({ turnId, estimate, message }) => ({
  type: CONFIRMATION_REQUIRED,
  payload: { turnId, estimate, message },
});

export const conversationUpserted = (conversation) => ({
  type: CONVERSATION_UPSERTED,
  payload: conversation,
});

export const conversationRemoved = (conversationId) => ({
  type: CONVERSATION_REMOVED,
  payload: { conversationId },
});

export const setActiveConversation = (conversationId, { loading = false } = {}) => ({
  type: SET_ACTIVE_CONVERSATION,
  payload: { conversationId, loading },
});

// ── Driven by the UI ────────────────────────────────────────────────────────

export const rememberLastConversation = (conversationId) => {
  try {
    if (conversationId) {
      localStorage.setItem(LAST_CONVERSATION_KEY, conversationId);
    } else {
      localStorage.removeItem(LAST_CONVERSATION_KEY);
    }
  } catch {
    // Private browsing, or storage disabled. Losing which conversation was
    // open costs the user one click; it is not worth failing anything for.
  }
};

export const recallLastConversation = () => {
  try {
    return localStorage.getItem(LAST_CONVERSATION_KEY);
  } catch {
    return null;
  }
};

/** The history list. */
export const loadConversations =
  ({ cursor } = {}) =>
  async (dispatch) => {
    dispatch({ type: SET_CONVERSATIONS_LOADING, payload: true });
    try {
      const data = await api.listConversations({ cursor });
      dispatch({
        type: SET_CONVERSATIONS,
        payload: {
          conversations: data.conversations || [],
          cursor: data.next_cursor,
          append: Boolean(cursor),
        },
      });
      return data;
    } catch (error) {
      dispatch({ type: SET_CONVERSATIONS_LOADING, payload: false });
      console.error("[Lou] Could not load conversations", error);
      return null;
    }
  };

/**
 * Opens a conversation and renders its transcript from the server.
 *
 * The commands a past turn emitted are deliberately not applied. The screen
 * the user is looking at now is not the screen that conversation was about,
 * and rewriting it because someone scrolled back through their history would
 * move the dashboard somewhere they never asked to go.
 *
 * Two opens can also be in flight at once — the list is a column of buttons and
 * nothing stops a second click while the first is still fetching. Responses
 * come back in whatever order the network gives them, so each one has to prove
 * it is still the conversation on screen before it renders. Without that check
 * a slow first request lands last and leaves the user reading one conversation
 * under another one's title.
 */
export const openConversation =
  (conversationId) =>
  async (dispatch, getState) => {
    dispatch(setActiveConversation(conversationId, { loading: true }));
    rememberLastConversation(conversationId);

    const stillOpen = () =>
      getState().chat?.activeConversationId === conversationId;

    try {
      const data = await api.listTurns(conversationId);
      // The conversation upsert is kept either way: it is keyed by id, so it
      // only ever refreshes that row's own title and timestamp.
      if (data.conversation) dispatch(conversationUpserted(data.conversation));
      if (!stillOpen()) return data;
      dispatch({ type: LOAD_TRANSCRIPT, payload: { turns: data.turns || [] } });
      return data;
    } catch (error) {
      console.error("[Lou] Could not open conversation", conversationId, error);
      if (stillOpen()) {
        dispatch({ type: LOAD_TRANSCRIPT, payload: { turns: [] } });
      }
      return null;
    }
  };

export const startNewConversation = () => async (dispatch) => {
  const conversation = await api.createConversation();
  dispatch(conversationUpserted(conversation));
  dispatch(setActiveConversation(conversation.id));
  dispatch({ type: LOAD_TRANSCRIPT, payload: { turns: [] } });
  rememberLastConversation(conversation.id);
  return conversation;
};

/** Renames a conversation. The backend echoes it to every other device. */
export const renameConversation =
  (conversationId, title) => async (dispatch) => {
    const updated = await api.renameConversation(conversationId, title);
    dispatch(conversationUpserted(updated));
    return updated;
  };

export const removeConversation = (conversationId) => async (dispatch, getState) => {
  await api.deleteConversation(conversationId);
  dispatch(conversationRemoved(conversationId));
  if (getState().chat?.activeConversationId === conversationId) {
    dispatch(setActiveConversation(null));
    rememberLastConversation(null);
  }
};

/**
 * Sends a message.
 *
 * Draws the turn immediately from the id the server hands back, rather than
 * waiting for the same news to arrive over the channel — the round trip is
 * real and the user is watching. `turnStarted` is idempotent, so when the
 * channel does deliver it, nothing is drawn twice.
 */
export const sendUserMessage =
  ({ conversationId, text, screenContext }) =>
  async (dispatch) => {
    const accepted = await api.sendMessage(conversationId, { text, screenContext });
    dispatch(
      turnStarted({
        turnId: accepted.turn_id,
        userText: text,
        turnSeq: accepted.seq,
      }),
    );
    return accepted;
  };

/** Re-sends a question the backend gated on cost, with the go-ahead. */
export const confirmDeepResearch =
  ({ conversationId, text, screenContext }) =>
  async (dispatch) => {
    const accepted = await api.sendMessage(conversationId, {
      text,
      screenContext,
      confirmDeepResearch: true,
    });
    dispatch(
      turnStarted({
        turnId: accepted.turn_id,
        userText: text,
        turnSeq: accepted.seq,
      }),
    );
    return accepted;
  };

/**
 * Refetches one turn whole.
 *
 * Used when the live stream has a hole in it — a `seq` that skipped. Patching
 * around missing pieces would mean guessing what was in them; refetching does
 * not.
 */
export const refetchTurn =
  ({ conversationId, turnId }) =>
  async (dispatch) => {
    try {
      const turn = await api.getTurn(conversationId, turnId);
      if (turn.status === "done") {
        dispatch(
          messageComplete({
            turnId: turn.turn_id,
            text: turn.answer_text || "",
            mode: turn.mode,
            traceId: turn.trace_id,
          }),
        );
      } else if (turn.status === "failed") {
        dispatch(
          turnFailed({
            turnId: turn.turn_id,
            errorCode: turn.error_code,
            message: turn.error_message || "That request did not finish.",
          }),
        );
      }
      return turn;
    } catch (error) {
      console.error("[Lou] Could not refetch turn", turnId, error);
      return null;
    }
  };

/**
 * Stops the turn that is running.
 *
 * Nothing is applied here. The store is changed by the
 * `conversation.turn_stopped` event that comes back on the channel, so every
 * device settles the same way and a stop pressed in one tab is visible in the
 * other — including the tab that did not press it.
 *
 * That means the reply keeps streaming for the moment it takes the backend to
 * reach its next await. Ending the turn locally first would be a lie the
 * other tab does not tell; the composer marks the press instead, which is
 * enough feedback and stays true.
 *
 * A turn that finished before the stop arrived is not an error, and nothing
 * is shown for it: the reply the user has is the reply they asked to stop.
 */
export const stopCurrentTurn =
  ({ conversationId, turnId }) =>
  async () => {
    try {
      return await api.stopTurn(conversationId, turnId);
    } catch (error) {
      console.error("[Lou] Could not stop turn", turnId, error);
      return null;
    }
  };

/**
 * Rates a turn, or clears the rating when the same thumb is pressed twice.
 *
 * Applied to the store first so the button responds under the finger, then
 * sent. It is stored against the turn, so it comes back with the transcript
 * rather than vanishing on reload.
 */
export const rateTurn =
  ({ conversationId, turnId, value, comment }) =>
  async (dispatch) => {
    dispatch({ type: SET_TURN_FEEDBACK, payload: { turnId, value } });
    try {
      await api.sendTurnFeedback(conversationId, turnId, value, comment);
      return true;
    } catch (error) {
      console.error("[Lou] Could not record feedback", turnId, error);
      dispatch({ type: SET_TURN_FEEDBACK, payload: { turnId, value: null } });
      return false;
    }
  };
